import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, History, Building2, CreditCard, Settings,
  Menu, LogOut, ShieldCheck, Wallet, ShieldAlert, TrendingUp, FolderArchive, Users, Bell, LayoutTemplate,
} from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LEGAL_PAGES } from "@/constants/legal";
import { OwnerTestMode } from "@/components/app/OwnerTestMode";
import api from "@/lib/api";

const UNREAD_POLL_MS = 30000;

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
  { to: "/app/new", label: "New Assessment", icon: FilePlus2, testid: "sidebar-nav-new" },
  { to: "/app/history", label: "Assessment History", icon: History, testid: "sidebar-nav-history" },
  { to: "/app/evidence", label: "Evidence Centre", icon: FolderArchive, testid: "sidebar-nav-evidence", plans: ["pro", "pm"] },
  { to: "/app/portfolio", label: "Portfolio", icon: TrendingUp, testid: "sidebar-nav-portfolio", plans: ["pm"] },
  { to: "/app/team", label: "Team", icon: Users, testid: "sidebar-nav-team", plans: ["pm"] },
  { to: "/app/properties", label: "Properties", icon: Building2, testid: "sidebar-nav-properties" },
  { to: "/app/pricing", label: "Pricing & Plans", icon: CreditCard, testid: "sidebar-nav-pricing" },
  { to: "/app/billing", label: "Billing", icon: Wallet, testid: "sidebar-nav-billing" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "sidebar-nav-settings" },
];

const PLAN_LABEL = { free: "Free plan", pro: "Pro Host", pm: "Property Manager" };

const SidebarContent = ({ onNavigate }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  // Poll unread admin-notification count for the badge (admins only).
  useEffect(() => {
    if (!user?.is_admin) return;
    let active = true;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/admin/notifications/unread-count");
        if (active) setUnread(r.data?.unread_count || 0);
      } catch (e) {
        /* silent — badge is non-critical */
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, UNREAD_POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex h-16 items-center border-b px-5">
        <Link to="/app/dashboard" onClick={onNavigate}>
          <Logo />
        </Link>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV.filter((item) => !item.plans || item.plans.includes(user?.plan)).map(({ to, label, icon: Icon, testid }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              onClick={onNavigate}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" />
              {label}
            </Link>
          );
        })}
        {user?.is_admin && (
          <>
            <Link
              to="/app/admin"
              data-testid="sidebar-nav-admin"
              onClick={onNavigate}
              className={`mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === "/app/admin"
                  ? "bg-primary/10 text-primary"
                  : "text-primary/80 hover:bg-primary/10 hover:text-primary"
              }`}
            >
              <ShieldAlert className="h-[18px] w-[18px]" />
              Admin
            </Link>
            <Link
              to="/app/admin/notifications"
              data-testid="sidebar-nav-notifications"
              onClick={onNavigate}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === "/app/admin/notifications"
                  ? "bg-primary/10 text-primary"
                  : "text-primary/80 hover:bg-primary/10 hover:text-primary"
              }`}
            >
              <Bell className="h-[18px] w-[18px]" />
              <span>Notifications</span>
              {unread > 0 && (
                <span
                  className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
                  data-testid="sidebar-notifications-badge"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Link>
            <Link
              to="/app/admin/content"
              data-testid="sidebar-nav-content"
              onClick={onNavigate}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === "/app/admin/content"
                  ? "bg-primary/10 text-primary"
                  : "text-primary/80 hover:bg-primary/10 hover:text-primary"
              }`}
            >
              <LayoutTemplate className="h-[18px] w-[18px]" />
              Content
            </Link>
            <Link
              to="/app/admin/analytics"
              data-testid="sidebar-nav-analytics"
              onClick={onNavigate}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === "/app/admin/analytics"
                  ? "bg-primary/10 text-primary"
                  : "text-primary/80 hover:bg-primary/10 hover:text-primary"
              }`}
            >
              <TrendingUp className="h-[18px] w-[18px]" />
              Analytics
            </Link>
          </>
        )}
      </nav>
      <div className="border-t p-3">
        <div className="mb-2 rounded-lg bg-accent/50 px-3 py-2.5">
          <p className="truncate text-sm font-medium text-foreground">{user?.business_name || user?.email}</p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> {PLAN_LABEL[user?.plan] || "Free plan"}
          </p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
          data-testid="sidebar-logout-button"
        >
          <LogOut className="h-[18px] w-[18px]" /> Log out
        </Button>
      </div>
    </div>
  );
};

export const AppShell = ({ title, action, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r lg:block">
        <SidebarContent />
      </aside>

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background px-4 sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <h1 className="font-display text-lg font-semibold text-foreground">{title}</h1>
          <div className="ml-auto">{action}</div>
        </header>

        <main className="mx-auto max-w-[1200px] p-4 sm:p-6 lg:p-8">{children}</main>

        <footer className="border-t">
          <div className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-3 px-4 py-5 sm:flex-row sm:px-6 lg:px-8">
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} HostGuard Australia</p>
            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
              {LEGAL_PAGES.map((p) => (
                <Link
                  key={p.slug}
                  to={p.path}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid={`app-footer-${p.slug}-link`}
                >
                  {p.footerLabel}
                </Link>
              ))}
            </nav>
          </div>
        </footer>
      </div>
      <OwnerTestMode />
    </div>
  );
};
